import Clarity from "@microsoft/clarity";
import * as Sentry from "@sentry/react";
import * as React from "react";
import ReactGA from "react-ga4";
import { useLocation } from "react-router-dom";
import { Profile } from "@/contexts/AuthContext.tsx";

class EmptyTracker {
	public init() {}

	public setUser(id: string, email: string) {}

	public trackEvent(category: string, action: string, label?: string) {}

	public trackPageView(path: string) {}
}

class Tracker {
	public constructor(
		private readonly clarityId: string,
		private readonly sentryDsn: string,
		private readonly gaMeasurementId?: string,
	) {}
	public init() {
		console.log("Initializing Clarity with ID:", this.clarityId);
		Clarity.init(this.clarityId);

		console.log("Initializing Sentry with DSN:", this.sentryDsn);
		Sentry.init({
			dsn: this.sentryDsn,
			environment: import.meta.env.VITE_PROJECT_ENVIRONMENT,
			release: import.meta.env.VITE_BUILD_COMMIT,
			replaysSessionSampleRate: 0.1,
			// If the entire session is not sampled, use the below sample rate to sample
			// sessions when an error occurs.
			replaysOnErrorSampleRate: 1.0,
			tracePropagationTargets: ["localhost", /^https:\/\/.*\.supabase\.co/],
			integrations: [
				Sentry.browserTracingIntegration({}),
				Sentry.replayIntegration({
					maskAllText: true,
					blockAllMedia: true,
				}),
				Sentry.captureConsoleIntegration({ levels: ["error"] }),
			],
		});

		// Initialize Google Analytics if measurement ID is provided
		if (this.gaMeasurementId) {
			console.log(
				"Initializing Google Analytics with ID:",
				this.gaMeasurementId,
			);
			ReactGA.initialize(this.gaMeasurementId, {
				testMode: import.meta.env.DEV, // Disable in development
			});
		}
	}

	public setUser(id: string, email: string) {
		Sentry.setUser({ id, email });
		Clarity.identify(id, undefined, undefined, email);

		if (this.gaMeasurementId) {
			ReactGA.set({ userId: id });
			ReactGA.set({ user_email: email });
		}
	}

	public trackEvent(category: string, action: string, label?: string) {
		if (this.gaMeasurementId) {
			ReactGA.event({
				category,
				action,
				label,
			});
		}
	}

	public trackPageView(path: string) {
		if (this.gaMeasurementId) {
			ReactGA.send({ hitType: "pageview", page: path });
		}
	}
}

export const tracker =
	import.meta.env.VITE_CLARITY_ID && import.meta.env.VITE_SENTRY_DSN
		? new Tracker(
				import.meta.env.VITE_CLARITY_ID,
				import.meta.env.VITE_SENTRY_DSN,
				import.meta.env.VITE_GA_MEASUREMENT_ID, // Optional
			)
		: new EmptyTracker();

export function useTrackUser(profile: Profile | null) {
	React.useEffect(() => {
		if (profile) {
			tracker.setUser(profile.id, profile.email);
		}
	}, [profile?.email, profile?.id]);
}

export function usePageTracking() {
	const location = useLocation();

	React.useEffect(() => {
		tracker.trackPageView(location.pathname + location.search);
	}, [location.pathname, location.search]);
}
